import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workspaceApi, ListWorkspaceTasksParams } from '../services/workspaceApi';
import type { WorkspaceCreate, WorkspaceUpdate, ListWorkspacesParams } from '../types';

export const workspaceKeys = {
  all: ['workspaces'] as const,
  lists: () => [...workspaceKeys.all, 'list'] as const,
  list: (params: ListWorkspacesParams) => [...workspaceKeys.lists(), params] as const,
  details: () => [...workspaceKeys.all, 'detail'] as const,
  detail: (id: string) => [...workspaceKeys.details(), id] as const,
  items: (workspaceId: string, params?: ListWorkspaceTasksParams) =>
    [...workspaceKeys.all, 'items', workspaceId, params] as const,
  itemCount: (workspaceId: string) => [...workspaceKeys.all, 'task-count', workspaceId] as const,
};

interface UseWorkspacesOptions extends ListWorkspacesParams {
  enabled?: boolean;
}

export function useWorkspaces(options: UseWorkspacesOptions = {}) {
  const { enabled = true, ...params } = options;

  return useQuery({
    queryKey: workspaceKeys.list(params),
    queryFn: () => workspaceApi.list(params),
    enabled,
    staleTime: 10_000,
  });
}

interface UseWorkspaceOptions {
  includeBoards?: boolean;
  includeMembers?: boolean;
  enabled?: boolean;
}

export function useWorkspace(id: string, options: UseWorkspaceOptions = {}) {
  const { enabled = true, ...queryParams } = options;

  return useQuery({
    queryKey: [...workspaceKeys.detail(id), queryParams],
    queryFn: () => workspaceApi.get(id, queryParams),
    enabled: enabled && !!id,
    staleTime: 10_000,
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkspaceCreate) => workspaceApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
      // A default board is auto-created with the workspace, refresh the boards list
      queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkspaceUpdate }) =>
      workspaceApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workspaceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });
}

interface UseWorkspaceTasksOptions extends ListWorkspaceTasksParams {
  enabled?: boolean;
}

export function useWorkspaceTasks(workspaceId: string, options: UseWorkspaceTasksOptions = {}) {
  const { enabled = true, ...params } = options;

  return useQuery({
    queryKey: workspaceKeys.items(workspaceId, params),
    queryFn: () => workspaceApi.listItems(workspaceId, params),
    enabled: enabled && !!workspaceId,
  });
}

export function useWorkspaceTaskCount(workspaceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workspaceKeys.itemCount(workspaceId),
    queryFn: () => workspaceApi.countItems(workspaceId),
    enabled: options?.enabled !== false && !!workspaceId,
  });
}
